import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import DataTable from '../components/DataTable';
import IconButton from '../components/IconButton';
import Spinner from '../components/Spinner';
import './Roles.css';

function Roles({ tenantId }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    if (tenantId) {
      loadRoles();
    }
  }, [tenantId]);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const data = await api.get('/roles', tenantId);
      setRoles(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading roles:', error);
      setError('Fehler beim Laden der Rollen');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);

    try {
      if (editingRole) {
        await api.put(`/roles/${editingRole.id}`, formData, tenantId);
        setSuccessMessage('Rolle erfolgreich aktualisiert');
      } else {
        await api.post('/roles', formData, tenantId);
        setSuccessMessage('Rolle erfolgreich erstellt');
      }
      await loadRoles();
      handleCancel();
    } catch (error) {
      console.error('Error saving role:', error);
      setError(error.response?.data?.error || 'Fehler beim Speichern der Rolle');
    }
  };

  const handleEdit = (role) => {
    setEditingRole(role);
    setFormData({
      name: role.name,
      description: role.description || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Möchten Sie die Rolle "${role.name}" wirklich löschen?`)) {
      return;
    }

    try {
      await api.delete(`/roles/${role.id}`, tenantId);
      setSuccessMessage('Rolle erfolgreich gelöscht');
      await loadRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
      setError(error.response?.data?.error || 'Fehler beim Löschen der Rolle');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRole(null);
    setFormData({ name: '', description: '' });
    setError(null);
  };

  if (loading) {
    return <div className="roles"><Spinner /></div>;
  }

  return (
    <div className="roles">
      <div className="page-header">
        <h1>Rollen</h1>
        <button
          onClick={() => setShowForm(true)}
          className="btn-primary"
          disabled={showForm}
        >
          Neue Rolle
        </button>
      </div>

      {successMessage && (
        <div className="success-message" style={{ marginBottom: '1rem' }}>
          {successMessage}
        </div>
      )}

      {error && (
        <div className="error-message" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {showForm && (
        <div className="form-container">
          <h2>{editingRole ? 'Rolle bearbeiten' : 'Neue Rolle'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Rollenname *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                disabled={editingRole?.isSystem}
              />
              {editingRole?.isSystem && (
                <p className="form-hint">System-Rollen können nicht bearbeitet werden</p>
              )}
            </div>

            <div className="form-group">
              <label>Beschreibung</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                disabled={editingRole?.isSystem}
              />
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={editingRole?.isSystem}>
                {editingRole ? 'Aktualisieren' : 'Erstellen'}
              </button>
              <button type="button" onClick={handleCancel} className="btn-secondary">
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      <DataTable
        data={roles}
        columns={[
          {
            key: 'name',
            header: 'Name',
            sortType: 'string',
            accessor: (row) => row.name
          },
          {
            key: 'description',
            header: 'Beschreibung',
            sortType: 'string',
            accessor: (row) => row.description || '-'
          },
          {
            key: 'isSystem',
            header: 'Typ',
            sortType: 'string',
            accessor: (row) => row.isSystem ? 'System' : 'Benutzerdefiniert',
            render: (row) => (
              <span className={row.isSystem ? 'system-badge' : 'custom-badge'}>
                {row.isSystem ? 'System' : 'Benutzerdefiniert'}
              </span>
            )
          },
          {
            key: 'actions',
            header: 'Aktionen',
            sortType: 'none',
            accessor: () => '',
            render: (row) => (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {!row.isSystem && (
                  <>
                    <IconButton
                      icon="edit"
                      onClick={() => handleEdit(row)}
                      title="Bearbeiten"
                      className="primary"
                    />
                    <IconButton
                      icon="delete"
                      onClick={() => handleDelete(row)}
                      title="Löschen"
                      className="danger"
                    />
                  </>
                )}
                {row.isSystem && (
                  <span style={{ color: '#999', fontSize: '0.875rem' }}>Geschützt</span>
                )}
              </div>
            )
          }
        ]}
      />
    </div>
  );
}

export default Roles;

